/**
 * How strong a client result is as proof, and which result belongs to which
 * reader.
 *
 * Pure, model-free. WHY THIS EXISTS (ITC Tax Brief run e2f0d912, 2026-10-01):
 * the data-centers page, a second brand-owned source, stated "$6 million annual
 * tax reduction representing a 40% decrease in assessed value" and "$3.3
 * million ... a 34% decrease", the strongest proof either page had. Neither
 * reached an angle: they sat past the 20-claim cap of their own page, the tile
 * shape was not recognised, and the sentence form was not a "result". When a
 * result DID get through, the order was the order of the page, so a $2M
 * one-off outranked a $6M-a-year figure with a 40% reduction in the same
 * industry as the reader.
 *
 * The score is the sum of four auditable parts, so a person can see why one
 * result outranked another:
 *   - magnitude: the log of the largest money figure (a valuation reduction
 *     counts for less than cash saved: it is an assessed value, not money kept)
 *   - percentage: a reduction in percent, bigger is stronger
 *   - recurrence: "annually", "per year" is a figure that repeats
 *   - client context: a named kind of client and a place make a result checkable
 * Relevance to the reader is added separately (`relevanceTo`): a data-center
 * audience gets data-center proof.
 */

import { FIGURE_SOURCE, canonicalFigure } from './result-evidence';

const RECURRENCE = /\b(?:annual(?:ly)?|per year|a year|each year|yearly|per annum)\b/i;
const VALUATION_RESULT = /\b(?:valuation|assessed value|assessment)\b/i;

const MONEY_ONLY = new RegExp(
  String.raw`[$£€]\s?\d[\d,]*(?:\.\d+)?\s?(?:[KkMmBb]\b|million\b|billion\b|thousand\b)?`,
  'g',
);
const PERCENT_ONLY = /(\d[\d,]*(?:\.\d+)?)\s?%/g;

/** Words that name the industry of a result or an audience, never its category of outcome. */
const GENERIC_WORDS = new Set([
  'tax',
  'taxes',
  'property',
  'savings',
  'saving',
  'saved',
  'reduction',
  'reductions',
  'valuation',
  'valuations',
  'owners',
  'owner',
  'operators',
  'operator',
  'leaders',
  'leaders',
  'leads',
  'lead',
  'client',
  'clients',
  'annually',
  'portfolio',
  'executives',
  'senior',
  'financial',
  'finance',
  'managing',
  'responsible',
  'compliance',
  'legal',
  'infrastructure',
  'with',
  'that',
  'this',
  'from',
  'their',
  'they',
  'your',
  'the',
  'and',
  'for',
  'per',
  'year',
  'service',
  'services',
  'multi',
  'state',
  'global',
  'international',
]);

function moneyValues(text: string): number[] {
  const values: number[] = [];
  for (const match of text.matchAll(MONEY_ONLY)) {
    const canonical = canonicalFigure(match[0].trim());
    if (canonical && /^[$£€]/.test(canonical)) values.push(Number(canonical.slice(1)));
  }
  return values;
}

function percentValues(text: string): number[] {
  return [...text.matchAll(PERCENT_ONLY)].map((match) => Number(match[1]!.replace(/,/g, '')));
}

export interface ResultStrength {
  total: number;
  magnitude: number;
  percentage: number;
  recurrence: number;
  context: number;
}

/**
 * The unit of "client context" a tile carries: a leading label ("Data Centers",
 * "International Data Center Developer") and, for a four-part tile, a trailing
 * place. A sentence carries none of it.
 */
function contextParts(claim: string): { label: boolean; place: boolean; qualifier: boolean } {
  const parts = claim
    .replace(/\s+/g, ' ')
    .trim()
    .split(/\.\s+/)
    .map((part) => part.replace(/\.$/, '').trim())
    .filter(Boolean);
  const looksLikeTile = parts.length >= 3 && parts.every((part) => part.split(/\s+/).length <= 8);
  if (!looksLikeTile) return { label: false, place: false, qualifier: false };
  const figureAt = parts.findIndex((part) => new RegExp(FIGURE_SOURCE).test(part));
  const first = parts[0] ?? '';
  const hasQualifier = /\b(?:multi-state|portfolio|international|global|national)\b/i.test(
    parts.slice(0, 2).join(' '),
  );
  return {
    label: figureAt >= 1,
    place: parts.length >= 4 && figureAt >= 1 && figureAt < parts.length - 2 && first.length > 0,
    qualifier: hasQualifier,
  };
}

export function resultStrength(claim: string): ResultStrength {
  const money = moneyValues(claim);
  const largest = money.length > 0 ? Math.max(...money) : 0;
  const valuation = VALUATION_RESULT.test(claim) && !/\bsavings?\b/i.test(claim);
  const magnitude =
    largest > 0 ? Math.max(0, Math.log10(largest) - 4) * 1.5 * (valuation ? 0.6 : 1) : 0;

  const percents = percentValues(claim);
  const biggestPercent = percents.length > 0 ? Math.max(...percents) : 0;
  const percentage =
    biggestPercent >= 30 ? 1.5 : biggestPercent >= 10 ? 1 : biggestPercent > 0 ? 0.5 : 0;

  const recurrence = RECURRENCE.test(claim) ? 1 : 0;

  const parts = contextParts(claim);
  // A result with no named kind of client cannot be attributed to anyone in
  // particular, which is the weakest thing proof can be: it is scored below a
  // tile that names the client, whatever its figure.
  const context = parts.label ? 1 + (parts.place ? 0.5 : 0) + (parts.qualifier ? 0.5 : 0) : -1;

  return {
    total: magnitude + percentage + recurrence + context,
    magnitude,
    percentage,
    recurrence,
    context,
  };
}

function industryWords(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .split(/\s+/)
      .filter((word) => word.length >= 4 && !GENERIC_WORDS.has(word))
      .map((word) => (word.length > 4 && word.endsWith('s') ? word.slice(0, -1) : word)),
  );
}

/**
 * True when the result and the audience share an industry word that is not
 * generic to the whole offer ("tax" is on every ITC result). "Data Centers.
 * $5M" and "Data Center Owners and Operators" share "data" and "center".
 */
export function relevanceTo(claim: string, audienceText: string): boolean {
  const audience = industryWords(audienceText);
  if (audience.size === 0) return false;
  for (const word of industryWords(claim)) {
    if (audience.has(word)) return true;
  }
  return false;
}

/** Added to a result's strength when it speaks to the audience's own industry. */
export const INDUSTRY_RELEVANCE_BONUS = 3;

export function scoreResultForAudience(claim: string, audienceText: string | null): number {
  const base = resultStrength(claim).total;
  return audienceText && relevanceTo(claim, audienceText) ? base + INDUSTRY_RELEVANCE_BONUS : base;
}

/**
 * True when two claims report the same engagement: they share a money figure.
 * The data-centers page prints one result twice, as the tile "International Data
 * Center Developer. Multi-State Portfolio 40% reduction. $6M annually ..." and as
 * the sentence "ITC Tax has documented ... a $6 million annual tax reduction ...".
 * Counting them as two results handed the tile to the data-center audience and
 * the sentence to the Tax Directors (ITC run ca9919d4). A claim with no money
 * figure is compared on its percentages.
 */
export function sameResult(a: string, b: string): boolean {
  const moneyA = moneyValues(a);
  const moneyB = moneyValues(b);
  if (moneyA.length > 0 && moneyB.length > 0) return moneyA.some((value) => moneyB.includes(value));
  const percentA = percentValues(a);
  const percentB = percentValues(b);
  return percentA.length > 0 && percentA.some((value) => percentB.includes(value));
}

/** Results strongest first. Ties keep the order the page gave them. */
export function rankResultsByStrength(claims: readonly string[]): string[] {
  return claims
    .map((claim, index) => ({ claim, index, total: resultStrength(claim).total }))
    .sort((a, b) => b.total - a.total || a.index - b.index)
    .map((entry) => entry.claim);
}

/**
 * One result per audience, no result used twice. Audiences whose own industry
 * has proof choose first (a data-center audience takes data-center proof before
 * a generic reader could take it); the rest take the strongest remaining.
 * `audiences` is in the order the caller wants served; the output is in the
 * same order.
 */
export function assignResultsToAudiences(
  results: readonly string[],
  allAudiences: readonly { name: string; text: string }[],
): { audience: string; result: string | null }[] {
  const remaining = [...results];
  const chosen = new Map<string, string | null>();
  const seenNames = new Set<string>();
  const audiences = allAudiences.filter((audience) => {
    const key = audience.name.trim().toLowerCase();
    if (seenNames.has(key)) return false;
    seenNames.add(key);
    return true;
  });

  const pick = (
    audience: { name: string; text: string },
    onlyRelevant: boolean,
    avoidOthers: boolean,
  ) => {
    const others = audiences.filter((other) => other.name !== audience.name);
    const candidates = remaining
      .map((claim, index) => ({
        claim,
        index,
        score: scoreResultForAudience(claim, audience.text),
      }))
      .filter((entry) => !onlyRelevant || relevanceTo(entry.claim, audience.text))
      .filter(
        (entry) => !avoidOthers || !others.some((other) => relevanceTo(entry.claim, other.text)),
      )
      .sort((a, b) => b.score - a.score || a.index - b.index);
    const best = candidates[0];
    if (!best) return;
    // The chosen result leaves with every restatement of it, so a reader never
    // receives the same engagement in other words.
    for (let i = remaining.length - 1; i >= 0; i -= 1) {
      if (remaining[i] === best.claim || sameResult(remaining[i]!, best.claim)) {
        remaining.splice(i, 1);
      }
    }
    chosen.set(audience.name, best.claim);
  };

  for (const audience of audiences) pick(audience, true, false);
  // An audience with no proof of its own industry takes the strongest result that
  // belongs to nobody else's industry; a data-center result is not a CFO's.
  for (const audience of audiences) if (!chosen.has(audience.name)) pick(audience, false, true);
  for (const audience of audiences) if (!chosen.has(audience.name)) pick(audience, false, false);

  return audiences.map((audience) => ({
    audience: audience.name,
    result: chosen.get(audience.name) ?? null,
  }));
}
